import { MapPin, User, Briefcase, BadgeCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { AgreementInfo, ContractorInfo } from '../types';
import { formatCNPJ } from '../utils/formatters';

interface AgreementInfoCardProps {
  info: AgreementInfo | undefined;
  loading: boolean;
  hasError: boolean;
}

const FALLBACK = '—';

function resolveEmployerName(c: ContractorInfo): string {
  return c.name ?? c.contractor?.name ?? FALLBACK;
}

function resolveEmployerDoc(c: ContractorInfo): string {
  const isCnpj = c.docType === 'CNPJ' || c.contractor?.docType === 'CNPJ';
  const raw = c.docValue ?? c.contractor?.docValue ?? '';
  return isCnpj ? formatCNPJ(raw) : raw || FALLBACK;
}

function resolveEmployerAddress(c: ContractorInfo): string {
  const addr = c.mainAddress ?? c.contractor?.mainAddress;
  if (addr?.city && addr?.region) return `${addr.city} — ${addr.region}`;
  return addr?.label ?? c.contractor?.mainAddress?.label ?? FALLBACK;
}

interface InfoRowProps {
  icon: React.FC<{ className?: string }>;
  label: string;
}

function InfoRow({ icon: Icon, label }: InfoRowProps) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
      <span className="text-xs text-slate-600 truncate">{label}</span>
    </div>
  );
}

export function AgreementInfoCard({ info, loading, hasError }: AgreementInfoCardProps) {
  const c = info?.contractor;
  const w = info?.worker;

  if (loading) {
    return (
      <Card className="mx-6 mb-3" aria-label="Carregando informações do acordo">
        <CardContent className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const employerName = !hasError && c ? resolveEmployerName(c) : FALLBACK;
  const initials = employerName
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();

  return (
    <Card className="mx-6 mb-3" aria-label="Informações do acordo">
      <CardContent>
        {hasError && (
          <p className="mb-2 text-xs text-amber-600">
            Dados do acordo incompletos. Verifique sua conexão.
          </p>
        )}
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white text-xs font-bold">
            {initials || '?'}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">{employerName}</p>
            {c && <p className="text-xs text-slate-500">{resolveEmployerDoc(c)}</p>}
          </div>
        </div>
        <div className="space-y-1 pl-0.5">
          {c && <InfoRow icon={MapPin} label={resolveEmployerAddress(c)} />}
          {w?.name && <InfoRow icon={User} label={w.name} />}
          {(w?.role ?? w?.jobRole) && <InfoRow icon={Briefcase} label={w.role ?? w.jobRole ?? ''} />}
          {!c && !w && <InfoRow icon={BadgeCheck} label="Dados do acordo não disponíveis" />}
        </div>
      </CardContent>
    </Card>
  );
}
